import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import { cn } from '@/shared/lib';
import type {
  TooltipProps,
  TooltipProviderProps,
  TooltipTriggerProps,
} from '@/shared/ui/tooltip/types';
import { tooltipStyles } from '@/shared/ui/tooltip/styles';

/**
 * Shares hover timing across every tooltip in a subtree.
 *
 * Optional: a tooltip works without a provider. Wrap a region in one when the
 * tooltips beneath it should share a delay rather than each re-deciding it.
 */
export function TooltipProvider({ children, delay, closeDelay }: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider delay={delay} closeDelay={closeDelay}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

/**
 * Root of the tooltip surface.
 *
 * Opens on hover and on keyboard focus, and closes on Escape. Focus is what
 * makes it a tooltip rather than decoration: an unlabelled icon button is
 * unusable without it.
 *
 * Children pass through untouched — the trigger and the content are siblings
 * and the primitive links them, so wrapping them here would mount the trigger
 * twice.
 */
export function Tooltip({ children, delay, closeDelay, ...rest }: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delay={delay} closeDelay={closeDelay}>
      <TooltipPrimitive.Root {...rest}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

/**
 * The element the tooltip describes. It must be focusable: a tooltip reachable
 * only by hover is unreachable by keyboard and by touch.
 */
export function TooltipTrigger({ className, type = 'button', ...props }: TooltipTriggerProps) {
  return (
    <TooltipPrimitive.Trigger
      className={cn(tooltipStyles.trigger, className)}
      type={type}
      {...props}
    />
  );
}